import React, { useEffect, useState } from "react";
import { Table, Button, Space, Modal, Form, Input, Switch, Card, Tabs } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

const { TabPane } = Tabs;

const CompanyManagement = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get("super-admin/companies");
      setCompanies(response.data);
    } catch (error) {
      toast.error("Failed to fetch companies");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCompany(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCompany(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await axios.patch(`super-admin/companies/${record.id}`, { status: !record.status });
      toast.success(`Company ${record.status ? "deactivated" : "activated"} successfully`);
      fetchCompanies();
    } catch (error) {
      toast.error("Failed to update company status");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingCompany) {
        await axios.put(`super-admin/companies/${editingCompany.id}`, values);
        toast.success("Company updated successfully");
      } else {
        await axios.post("super-admin/companies", values);
        toast.success("Company created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save company");
    }
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/super-admin/companies/${record.id}/view`)}
          >
            View Data
          </Button>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            {record.status ? "Deactivate" : "Activate"}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/dashboard")}
        style={{ marginBottom: "16px" }}
      >
        Back to Dashboard
      </Button>
      <Card
        title="Company Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Company
          </Button>
        }
      >
        <Table
          dataSource={companies}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingCompany ? "Edit Company" : "Create Company"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: "Please input company name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="tag_line" label="Tag Line">
            <Input />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: "Please input address!" }]}
          >
            <Input.TextArea />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: "Please input phone!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="website" label="Website">
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyManagement;
